import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchNotifications, markEventHandled, needsAction } from '../../lib/consoleNotifications';

const ICON = { message: '💬', request: '📄', cancelled: '🚫', accepted: '🤝', rejected: '✖️', purchase: '🛒', registered: '✅', extraction: '📦', error: '⚠️', support: '🎫', bounce: '📭', violation: '🚩' };
const FILTERS = [
  { key: 'open', ar: 'بانتظار التعامل', en: 'Needs action' },
  { key: 'all', ar: 'الكل', en: 'All' },
  { key: 'request', ar: 'طلبات التجار', en: 'Trader requests' },
  { key: 'message', ar: 'محادثات', en: 'Conversations' },
  { key: 'violation', ar: 'مخالفات تواصل', en: 'Contact violations' },
  { key: 'registered', ar: 'تسجيلات', en: 'Registrations' },
  { key: 'catalog', ar: 'كتالوجات', en: 'Catalogs' },
  { key: 'support', ar: 'دعم', en: 'Support' },
  { key: 'bounce', ar: 'مرتدّة', en: 'Bounces' },
];

const rel = (iso, isAr) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return isAr ? 'الآن' : 'now';
  if (mins < 60) return isAr ? `${mins} د` : `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return isAr ? `${h} س` : `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return isAr ? `${d} يوم` : `${d}d`;
  return isAr ? `${Math.floor(d / 30)} شهر` : `${Math.floor(d / 30)}mo`;
};

export default function ConsoleNotifications({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('open');
  const [busy, setBusy] = useState(null);

  useEffect(() => { fetchNotifications().then(setRows).catch(() => setRows([])); }, []);

  const counts = useMemo(() => {
    const c = { all: 0 };
    (rows || []).forEach((r) => {
      c.all++; c[r.group] = (c[r.group] || 0) + 1;
      if (needsAction(r)) c.open = (c.open || 0) + 1;
    });
    return c;
  }, [rows]);

  // "Needs action" is trader events nobody has marked handled, plus contact
  // violations nobody has reviewed. Only those two carry a handled state
  // (admin_events rows an `id`, violations a `violationId`); the derived items
  // (registrations, catalogs…) have none and stay under their own filters.
  const shown = (rows || []).filter((r) => (filter === 'all' ? true
    : filter === 'open' ? needsAction(r) : r.group === filter));

  const toggleHandled = async (e, r) => {
    e.stopPropagation();
    setBusy(r.id);
    try {
      await markEventHandled(r.id, !r.handled);
      setRows((list) => list.map((x) => (x.id === r.id ? { ...x, handled: !r.handled } : x)));
    } catch (err) {
      alert(isAr ? `تعذّر الحفظ: ${err.message}` : `Could not save: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="notifs">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', maxWidth: 820 }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{isAr ? 'الإشعارات' : 'Notifications'}</h1>
            <p className="ac-sub">{isAr ? 'كل ما يحدث على المنصّة في مكان واحد.' : 'Everything happening on the platform, in one inbox.'}</p>
          </div>
          <button className="ac-btn ac-btn-sm" onClick={() => { setRows(null); fetchNotifications().then(setRows).catch(() => setRows([])); }}>
            {isAr ? 'تحديث' : 'Refresh'}
          </button>
        </div>

        <div className="ac-chiprow" style={{ marginBottom: 18 }}>
          {FILTERS.map((f) => (
            <button key={f.key} className={`ac-chip${filter === f.key ? ' on' : ''}`} onClick={() => setFilter(f.key)}>
              {isAr ? f.ar : f.en}{counts[f.key] ? <span style={{ opacity: 0.6, marginInlineStart: 4 }}>{counts[f.key]}</span> : null}
            </button>
          ))}
        </div>

        {rows == null ? (
          <div className="ac-skel" style={{ height: 90 }} />
        ) : shown.length === 0 ? (
          <div className="ac-placeholder">{isAr ? 'لا شيء هنا بعد.' : 'Nothing here yet.'}</div>
        ) : shown.map((r, i) => (
          <div key={r.id || r.violationId || i} role="button" tabIndex={0} className="ac-card" onClick={() => r.link && nav(r.link)}
            onKeyDown={(e) => { if (e.key === 'Enter' && r.link) nav(r.link); }}
            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, marginBottom: 8, width: '100%', textAlign: isAr ? 'right' : 'left', cursor: r.link ? 'pointer' : 'default', border: '1px solid var(--ac-line)', fontFamily: 'inherit', opacity: r.handled ? 0.6 : 1 }}>
            <span style={{ fontSize: 20, flexShrink: 0 }} aria-hidden>{ICON[r.type] || '•'}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span style={{ display: 'block', fontSize: 13.5, fontWeight: 650, color: 'var(--ac-ink)' }}>{isAr ? r.title_ar : r.title_en}</span>
              {r.sub ? <span style={{ display: 'block', fontSize: 12, color: 'var(--ac-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.sub}</span> : null}
            </span>
            {r.tone && <span className={`ac-badge ${r.tone}`} style={{ height: 8, width: 8, padding: 0, borderRadius: '50%' }} />}
            <span style={{ fontSize: 11.5, color: 'var(--ac-faint)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{rel(r.at, isAr)}</span>
            {r.id && (
              <button className={`ac-btn ac-btn-sm${r.handled ? '' : ' ac-btn-primary'}`} disabled={busy === r.id}
                onClick={(e) => toggleHandled(e, r)} style={{ flexShrink: 0 }}>
                {r.handled ? (isAr ? 'تراجع' : 'Undo') : (isAr ? 'تم التعامل' : 'Handled')}
              </button>
            )}
            {/* A violation is settled on its own page (dismiss / warn), never by
                «تم التعامل», which writes admin_events. */}
            {r.violationId && !r.handled && (
              <button className="ac-btn ac-btn-sm ac-btn-primary" style={{ flexShrink: 0 }}
                onClick={(e) => { e.stopPropagation(); nav(r.link); }}>
                {isAr ? 'مراجعة' : 'Review'}
              </button>
            )}
          </div>
        ))}
      </div>
    </ConsoleShell>
  );
}
